/**
 * Message utility functions extracted from MessageDataService.
 * Used by reusable components (MessageStatusIcon, MessageList, useMessageSending).
 *
 * The original MessageDataService.ts class wrapper will be deleted with V2.
 * These standalone functions survive the transition.
 */

import type { Message } from '@/types/message-types';

/**
 * Clean corrupted field data from database
 */
export function cleanCorruptedField(fieldValue: unknown, fieldName: string, instanceId: string): string | null {
  if (typeof fieldValue === 'string' && fieldValue.length > 0) return fieldValue;
  if (fieldValue && typeof fieldValue === 'object') {
    const obj = fieldValue as Record<string, unknown>;
    if (obj._type === 'undefined' || obj.value === 'undefined') return null;
    if (obj.value && typeof obj.value === 'string') return obj.value;
    return null;
  }
  if (fieldValue !== null && fieldValue !== undefined && typeof fieldValue !== 'string') return null;
  return fieldValue;
}

/**
 * Clean and validate a single message from database
 */
export function cleanSingleMessage(rawMessage: Record<string, unknown>, instanceId: string): Message {
  const cleanWhatsappId = cleanCorruptedField(rawMessage.external_id, 'external_id', instanceId);
  const cleanDeliveredAt = cleanCorruptedField(rawMessage.delivered_at, 'delivered_at', instanceId);
  const cleanReadAt = cleanCorruptedField(rawMessage.read_at, 'read_at', instanceId);

  let status: Message['status'] = 'sent';
  const isOutgoing = rawMessage.sender_type === 'human_agent' ||
                    rawMessage.sender_type === 'ai_agent_automatic' ||
                    rawMessage.sender_type === 'ai_with_human_review_no_changes' ||
                    rawMessage.sender_type === 'ai_with_human_review_with_changes';

  if (isOutgoing) {
    if (!cleanWhatsappId) {
      status = rawMessage.status === 'failed' ? 'failed' : 'sending';
    } else if (cleanReadAt) {
      status = 'read';
    } else if (cleanDeliveredAt) {
      status = 'delivered';
    } else {
      status = 'sent';
    }
  } else {
    status = 'delivered';
  }

  return {
    id: rawMessage.id,
    conversation_id: rawMessage.conversation_id,
    sender_type: rawMessage.sender_type,
    sender_id: rawMessage.sender_id,
    content: rawMessage.content,
    message_type: rawMessage.message_type || 'text',
    external_id: cleanWhatsappId,
    media_url: rawMessage.media_url || null,
    metadata: rawMessage.metadata,
    is_read: rawMessage.is_read || false,
    created_at: rawMessage.created_at,
    delivered_at: cleanDeliveredAt,
    read_at: cleanReadAt,
    status,
    ai_model: rawMessage.ai_model || null,
    ai_suggestion_data: rawMessage.ai_suggestion_data || null,
    change_detection_metadata: rawMessage.change_detection_metadata || null
  };
}

/**
 * Create an optimistic message for immediate UI feedback
 */
export function createOptimisticMessage(
  conversationId: string,
  senderId: string,
  content: string,
  messageType: string = 'text',
  aiModel?: string,
  senderType: string = 'human_agent',
  aiSuggestionData?: Record<string, unknown>
): Message {
  return {
    id: `optimistic-${Date.now()}-${Math.random()}`,
    conversation_id: conversationId,
    sender_type: senderType as string,
    sender_id: senderId,
    content,
    message_type: messageType as Message['message_type'],
    external_id: null,
    metadata: aiSuggestionData ? { isOptimistic: true, ...aiSuggestionData } : null,
    is_read: false,
    created_at: new Date().toISOString(),
    delivered_at: null,
    read_at: null,
    status: 'sending',
    ai_model: aiModel || null,
    ai_suggestion_data: aiSuggestionData || null,
    change_detection_metadata: aiSuggestionData?.changeMetadata || null
  };
}

/**
 * Sort messages by creation date (oldest first)
 */
export function sortMessagesByDate(messages: Message[]): Message[] {
  if (!Array.isArray(messages)) return [];
  return [...messages].sort((a, b) => {
    const getDateString = (dateValue: unknown): string => {
      if (typeof dateValue === 'string') return dateValue;
      if (dateValue && typeof dateValue === 'object' && dateValue.value) return dateValue.value;
      return '';
    };
    const dateA = new Date(getDateString(a.created_at));
    const dateB = new Date(getDateString(b.created_at));
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
    return dateA.getTime() - dateB.getTime();
  });
}

/**
 * Get message status display info (icon, color, tooltip)
 */
export function getMessageStatusInfo(message: Message): {
  icon: string;
  color: string;
  tooltip: string;
} {
  const isOutgoing = message.sender_type === 'human_agent' ||
                    message.sender_type === 'ai_agent_automatic' ||
                    message.sender_type === 'ai_with_human_review_no_changes' ||
                    message.sender_type === 'ai_with_human_review_with_changes';

  switch (message.status) {
    case 'sending':
      return { icon: 'clock', color: 'text-muted-foreground', tooltip: 'Sending message...' };
    case 'sent':
      return { icon: 'check', color: 'text-muted-foreground', tooltip: isOutgoing ? 'Message sent' : 'Message received' };
    case 'delivered':
      return { icon: 'check-check', color: 'text-muted-foreground', tooltip: isOutgoing ? 'Message delivered' : 'Message received' };
    case 'read':
      return { icon: 'check-check', color: 'text-primary', tooltip: 'Message read' };
    case 'failed':
      return { icon: 'x-circle', color: 'text-destructive', tooltip: 'Failed to send message' };
    default:
      return { icon: 'clock', color: 'text-muted-foreground', tooltip: 'Unknown status' };
  }
}

/**
 * Extract the WhatsApp interactive payload from a message.
 *
 * Prefers `metadata.interactive` (the post-PR-B1b structured envelope) and falls
 * back to JSON.parse on `content` for legacy rows where the writer dumped the
 * whole interactive object as a JSON string. Returns null if neither yields an
 * envelope or the candidate isn't an object.
 *
 * Contract: when `metadata.interactive` is **present**, it is the source of truth.
 * If present-and-valid → returned. If present-but-malformed (array, primitive,
 * null) → hard-fail with null (do NOT fall through to content; metadata claimed
 * to have the payload, so trusting `content` instead would mask the writer bug).
 * Only when `metadata.interactive` is genuinely **absent** does the helper fall
 * back to parsing `content`.
 *
 * Accepts a structural shape rather than a specific Message type so it works
 * with both V1 (`src/types/message-types.ts`) and V2
 * (`src/modules/conversations/types/conversation-types.ts`) Message types,
 * which differ in `content` nullability across the V2 discriminated union.
 *
 * Reference: `docs/mods/conversations-v2/MESSAGES_CONTENT_CONTRACT.md`
 */
export function getInteractivePayload(
  message: { content: unknown; metadata: Record<string, unknown> | null }
): Record<string, unknown> | null {
  const meta = message.metadata;

  // metadata.interactive is the source of truth when present.
  if (meta && 'interactive' in meta) {
    const metaInteractive = meta.interactive;
    if (metaInteractive && typeof metaInteractive === 'object' && !Array.isArray(metaInteractive)) {
      return metaInteractive as Record<string, unknown>;
    }
    // Present but malformed — hard-fail. Don't fall through to content.
    return null;
  }

  // metadata.interactive genuinely absent → parse content (legacy rows store JSON string).
  if (typeof message.content === 'string') {
    try {
      const parsed: unknown = JSON.parse(message.content);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const obj = parsed as Record<string, unknown>;
        const inner = obj.interactive;
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          return inner as Record<string, unknown>;
        }
        return obj;
      }
    } catch {
      // unparseable content → no payload
    }
  }

  return null;
}
